import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { useCallback, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { CatalogPlaceBidButton } from '@/components/GridItemCard/Card/CardOptions/CatalogItemCard/components/CatalogPlaceBidButton/CatalogPlaceBidButton';
import ItemCardBadge from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardEstimate from '@/components/GridItemCard/Card/components/ItemCardEstimate/ItemCardEstimate';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import ItemCardLiveButton from '@/components/ItemCard/components/ItemCardLiveButton';
import ImageRow from '@/components/ItemCard/ImageRow';
import PriceRow from '@/components/ItemCard/PriceRow';
import { useIsBrowsingPage } from '@/hooks/useIsBrowsingPage';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useAppSelector } from '@/redux/hooks';
import { getLiveCatalogCurrentItemId, getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsCatalogOnly,
    getItemSummarySaleStartTs,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { shouldShowLive } from '@/utils/live';

import { ItemCardComponent, ItemCardInfo } from '../../../types/types';

const CatalogItemCard: ItemCardComponent = ({ className, dimension, itemId }) => {
    const [linkHover, setLinkHover] = useState(false);

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const isLive = comingUp && showLive && currentItem === itemId;
    const isLotOpen = currentItem === itemId;

    const isBrowsingPage = useIsBrowsingPage();
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);

    const { onNavigateInitiated } = useItemCardNavigation(itemId);

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    return (
        <CatalogItemCardContainer
            $dimension={dimension}
            $open={isLotOpen}
            $placeholder={!title}
            className={className}
            data-testid={`catalog-item-card-${itemId}`}
            key={`${itemId}-noWidth-$`}
        >
            <PlacedItemCardSaveIcon>
                <ItemCardSaveIcon
                    itemId={itemId}
                    labeled={!isMobile && isBrowsingPage}
                />
            </PlacedItemCardSaveIcon>
            <ItemCardBadge
                allowBidding={isLive}
                itemId={itemId}
                savedItemsCard
            />

            <PlacedImageContainer
                onMouseEnter={enterLinkHoverState}
                onMouseLeave={leaveLinkHoverState}
            >
                <ImageRow
                    isCatalogPage
                    itemId={itemId}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.ImageClick);
                    }}
                    showBidStatus
                    showImageWithHover={useIsWiderThan(tabletNarrowWidthNum)}
                />
            </PlacedImageContainer>
            <PlacedItemCardLiveButton>
                <ItemCardLiveButton
                    allowBidding
                    isCatalogPage
                    itemId={itemId}
                    showDate={false}
                />
            </PlacedItemCardLiveButton>
            <PlacedItemCardTitle
                $addMargin={!comingUp && !isLive}
                isHovered={linkHover}
                itemId={itemId}
                lineCount={2}
                onMouseEnter={enterLinkHoverState}
                onMouseLeave={leaveLinkHoverState}
                onNavigate={() => {
                    onNavigateInitiated(ItemCardNavigationTrigger.TitleClick);
                }}
                showLotNumber
            />
            <PlacedItemCardEstimate itemId={itemId} />
            <PlacedPriceRow
                isCatalogCard
                itemId={itemId}
                onNavigate={() => {
                    onNavigateInitiated(ItemCardNavigationTrigger.PriceClick);
                }}
            />
            <CatalogPlaceBidButton itemId={itemId} />
        </CatalogItemCardContainer>
    );
};

const info: ItemCardInfo = { ItemCardComponent: CatalogItemCard };

export default info;

const PlacedImageContainer = styled.div`
    grid-area: img;
    max-width: 100%;
    position: relative;
`;

const PlacedItemCardEstimate = styled(ItemCardEstimate)`
    padding-top: 5px;
    grid-area: estimate;
`;

const PlacedItemCardLiveButton = styled.div`
    grid-area: live;
`;

const PlacedPriceRow = styled(PriceRow)`
    grid-area: price;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)<{ $addMargin: boolean }>`
    grid-area: title;
    display: flex;
    align-items: center;
    ${({ $addMargin }) => {
        if ($addMargin) {
            return css`
                margin-top: 10px;
            `;
        }
    }}
`;

const PlacedItemCardSaveIcon = styled.div`
    position: absolute;
    top: 9px;
    right: 9px;
    z-index: 2;
`;

interface StyledCatalogItemCardProps {
    $dimension: number;
    $open: boolean;
    $placeholder: boolean;
}

const CatalogItemCardContainer = styled.div<StyledCatalogItemCardProps>`
    position: relative;
    justify-self: center;
    overflow: hidden;
    display: grid;
    width: inherit;
    grid-template-columns: ${({ $dimension }) => ($dimension ? `${$dimension}px` : '1fr')};
    grid-template-rows: min-content min-content min-content min-content min-content;
    grid-template-areas:
        'img'
        'live'
        'title'
        'estimate'
        'price';

    transition: all 0.15s;

    ${({ $open, theme }) => {
        if ($open) {
            return css`
                @media (min-width: ${theme.breakpoints.tabletWidthNum + 1}px) {
                    background-color: ${theme.colors.grey500};
                    border-radius: 3px;
                }
            `;
        }
    }}

    ${({ $placeholder, theme }) =>
        $placeholder &&
        `
        img {
            filter: blur(5px) !important;
        }

        div,
        span,
        h4 {
            color: transparent !important;
            text-shadow: 0 0 10px ${theme.colors.shadow500} !important;
        }
    `}
`;
